/**
 * Centralised access to `import.meta.env.VITE_*` so the rest of the codebase
 * never touches `import.meta.env` (or, heaven forbid, `process.env`) directly.
 *
 * If you find yourself reading an env var that isn't here, add it to
 * `.env.example`, the `ImportMetaEnv` interface in `src/vite-env.d.ts`, and
 * expose a typed getter below.
 */
import { normalizeSiteUrl } from './site';

function required(name: keyof ImportMetaEnv, fallback?: string): string {
  const raw = import.meta.env[name] as string | undefined;
  if (typeof raw === 'string' && raw.length > 0) return raw;
  if (fallback !== undefined) return fallback;
  // Hard-fail in production builds — easier to spot at deploy time than 404s
  // during a deep-link redirect.
  throw new Error(`Missing required env var: ${name}`);
}

function optional(name: keyof ImportMetaEnv): string | undefined {
  const raw = import.meta.env[name] as string | undefined;
  return typeof raw === 'string' && raw.length > 0 ? raw : undefined;
}

/**
 * Sentry DSN is public-safe (it only authorizes sending events), so it ships
 * with a baked default and is overridable per deploy via VITE_SENTRY_DSN.
 * Project id 6 on the self-hosted instance.
 */
const DEFAULT_SENTRY_DSN = 'https://0999bfaeb985b010cd6fd88132756d08@sentry.lucycare.co.kr/6';

export const env = {
  appScheme: (): string => required('VITE_APP_SCHEME', 'lucycare'),
  iosStoreUrl: (): string => required('VITE_IOS_APP_STORE_URL'),
  androidPlayUrl: (): string => required('VITE_ANDROID_PLAY_URL'),
  apiBase: (): string => required('VITE_API_BASE'),
  // Normalised (trailing slash stripped, production origin as the fallback) by
  // the same helper vite.config.ts uses, so runtime canonicals can't drift from
  // the baked-in <head>, the sitemap, or robots.txt.
  siteUrl: (): string => normalizeSiteUrl(optional('VITE_SITE_URL')),
  gtmId: (): string | undefined => optional('VITE_GTM_ID'),
  gaId: (): string | undefined => optional('VITE_GA_ID'),
  sentryDsn: (): string => optional('VITE_SENTRY_DSN') ?? DEFAULT_SENTRY_DSN,
  // Defaults to Vite's build mode ('production' for `vite build`); CI overrides
  // it to 'development' for the staging deploy so Sentry can filter the two.
  sentryEnvironment: (): string => optional('VITE_SENTRY_ENVIRONMENT') ?? import.meta.env.MODE,
  sentryTracesSampleRate: (): number => {
    const raw = optional('VITE_SENTRY_TRACES_SAMPLE_RATE');
    const parsed = raw === undefined ? NaN : Number(raw);
    return Number.isFinite(parsed) ? parsed : import.meta.env.PROD ? 0.2 : 1;
  },
  sentryRelease: (): string | undefined => optional('VITE_SENTRY_RELEASE'),
};
